import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  useMediaQuery,
  CircularProgress,
  Alert,
  Chip,
  Paper,
  Grid,
  Stack,
  Fade,
  Grow,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

// Componentes propios
import AppLayout from '../components/layout/AppLayout';
import { SearchBar } from '../components/common';
import {
  SubscriptionStats,
  SubscriptionCard,
  SubscriptionsTable,
} from '../components/subscriptions';
import SubscriptionFormModal from '../components/forms/SubscriptionFormModal';
import ConfirmationModal from '../components/forms/ConfirmationModal';
import SubscriptionTemplates from '../components/subscriptions/SubscriptionTemplates';
import ReadOnlyBanner from '../components/common/ReadOnlyBanner';

// Hooks y contextos
import { useSubscriptions, useContacts, useSubscriptionTemplates, useSearchAndFilter } from '../hooks';
import { useAuth } from '../contexts/AuthContext';
import usePageOperations from '../hooks/usePageOperations';

// Utils y Constantes
import { filterSubscriptions } from '../utils/subscriptionUtils';
import { THEME_COLORS, OPERATION_STATUS, SUBSCRIPTION_STATUS, BREAKPOINTS } from '../constants';
import { canManageSubscriptions } from '../utils/permissionUtils';

/**
 * Página principal de gestión de suscripciones
 */
const SubscriptionsPageComponent = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(BREAKPOINTS.MOBILE));

  const subscriptionsHook = useSubscriptions();
  const { subscriptions, stats, loading, error, pagination, goToPage, changePageSize } = subscriptionsHook;
  const { contacts } = useContacts();
  const {
    templates,
    loading: templatesLoading,
    error: templatesError,
    updateTemplate
  } = useSubscriptionTemplates();

  const {
    openCreateModal,
    openEditModal,
    openDeleteModal,
    FormModal,
    DeleteModal,
    OperationModal,
  } = usePageOperations({
    entityName: 'Suscripción',
    entityNamePlural: 'Suscripciones',
    crudFunctions: {
      create: subscriptionsHook.createSubscription,
      update: subscriptionsHook.updateSubscription,
      delete: subscriptionsHook.deleteSubscription,
    },
    FormModalComponent: SubscriptionFormModal,
    formModalProps: {
      availableContacts: contacts,
    },
    entityIdKey: 'subscription_id',
  });

  const {
    searchInput,
    setSearchInput,
    searchTerm,
    filterStatus,
    handleSearch,
    handleClearSearch,
    handleKeyPress,
    handleFilterChange,
  } = useSearchAndFilter();
  
  const [expandedSubscriptionId, setExpandedSubscriptionId] = useState(null);

  const filteredSubscriptions = useMemo(() => {
    const filtered = filterSubscriptions(subscriptions, searchTerm, filterStatus);
    return filtered;
  }, [subscriptions, searchTerm, filterStatus]);

  const handleExpand = useCallback((subscriptionId) => {
    setExpandedSubscriptionId(prev => (prev === subscriptionId ? null : subscriptionId));
  }, []);

  if (error) {
    return (
      <AppLayout currentPage="subscriptions">
        <Box sx={{ paddingY: 4 }}><Typography variant="h6" color="error" align="center">Error al cargar suscripciones: {error}</Typography></Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout currentPage="subscriptions">
      <Fade in timeout={500}>
        <Box>
          <Box sx={{ marginBottom: 4 }}>
            <Typography variant="h4" sx={{ color: THEME_COLORS.TEXT_PRIMARY, fontWeight: 700, fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
              Gestión de Suscripciones
            </Typography>
            <Typography variant="body1" sx={{ color: THEME_COLORS.TEXT_MUTED, fontSize: { xs: '0.875rem', sm: '1rem' }, mt: 1 }}>
              Controla los planes, fechas y estados de las suscripciones de tus contactos.
            </Typography>
          </Box>

          <Grow in timeout={700}>
            <Box>
              <SubscriptionStats stats={stats} />
            </Box>
          </Grow>

          <Grow in timeout={800}>
            <Box sx={{ my: 4 }}>
              <SubscriptionTemplates
                templates={templates}
                loading={templatesLoading}
                error={templatesError}
                onSave={updateTemplate}
              />
            </Box>
          </Grow>

          <Grow in timeout={900}>
            <Paper elevation={0} sx={{ p: 2, mb: 4, border: `1px solid ${THEME_COLORS.BORDER_LIGHT}`, borderRadius: '16px', backgroundColor: THEME_COLORS.BACKGROUND_PAPER }}>
              <Grid container>
                <Grid item xs={12}>
                  <SearchBar
                    searchInput={searchInput}
                    searchTerm={searchTerm}
                    placeholder="Buscar por nombre de contacto, plan o estado..."
                    onSearchInputChange={setSearchInput}
                    onSearch={handleSearch}
                    onClear={handleClearSearch}
                    onKeyPress={handleKeyPress}
                  />
                </Grid>
                <Grid item xs={12} sx={{ pt: 0.5 }}>
                  <Stack direction="row" spacing={1} justifyContent="flex-start">
                    <Chip 
                      label="Todas" 
                      onClick={() => handleFilterChange('all')} 
                      clickable 
                      variant={filterStatus === 'all' ? 'filled' : 'outlined'} 
                      sx={{ borderRadius: '8px', fontWeight: 500 }} 
                    />
                    <Chip 
                      label="Activas" 
                      onClick={() => handleFilterChange('activa')} 
                      clickable 
                      variant={filterStatus === 'activa' ? 'filled' : 'outlined'} 
                      sx={{ borderRadius: '8px', fontWeight: 500 }} 
                    />
                    <Chip 
                      label="Vencidas" 
                      onClick={() => handleFilterChange('vencida')} 
                      clickable 
                      variant={filterStatus === 'vencida' ? 'filled' : 'outlined'} 
                      sx={{ borderRadius: '8px', fontWeight: 500 }} 
                    />
                    <Chip 
                      label="Pronto por vencer" 
                      onClick={() => handleFilterChange('expiring_soon')} 
                      clickable 
                      variant={filterStatus === 'expiring_soon' ? 'filled' : 'outlined'} 
                      sx={{ borderRadius: '8px', fontWeight: 500 }} 
                    />
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          </Grow>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={openCreateModal}
              sx={{
                backgroundColor: THEME_COLORS.TEXT_PRIMARY, color: THEME_COLORS.BACKGROUND_PAPER, borderRadius: '12px',
                paddingX: 3, paddingY: 1.5, textTransform: 'none', fontWeight: 600, '&:hover': { backgroundColor: '#1a1a1a' }
              }}
            >
              Nueva Suscripción
            </Button>
          </Box>

          {isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              {filteredSubscriptions.map(s => (
                <SubscriptionCard
                  key={s.subscription_id}
                  subscription={s}
                  onEdit={() => openEditModal(s)}
                  onDelete={() => openDeleteModal(s)}
                  isExpanded={expandedSubscriptionId === s.subscription_id}
                  onExpand={handleExpand}
                  showActions={true}
                />
              ))}
            </Box>
          ) : (
            <SubscriptionsTable
              subscriptions={filteredSubscriptions}
              expandedSubscriptionId={expandedSubscriptionId}
              onExpand={handleExpand}
              onEdit={openEditModal}
              onDelete={openDeleteModal}
              showActions={true}
              // Props de paginación
              pagination={pagination}
              onPageChange={goToPage}
              onPageSizeChange={changePageSize}
            />
          )}
        </Box>
      </Fade>
      
      <FormModal />
      <DeleteModal />
      <OperationModal />
    </AppLayout>
  );
};

// Memoizar el componente para optimización
const SubscriptionsPage = React.memo(SubscriptionsPageComponent);
SubscriptionsPage.displayName = 'SubscriptionsPage';

export default SubscriptionsPage; 